// LabelsRegister.jsx: Labels register (2026-09-29), design "Labels (web)".
//
// Every heat, piece and consumable-lot label printed, reprinted or voided,
// from GET /api/labels (tenant scoped). Reprint and void each take a reason
// and are logged by the server. Labels are never deleted; a voided label
// keeps its history.

import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import {
  Container, Card, Stack, Typography, Button, TextField, Box, Grid, Alert, LinearProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, ToggleButton, ToggleButtonGroup,
} from "@mui/material";
import PageGuide from "../../components/PageGuide";
import ApiCall from "../../utils/apicall";
import { StatusPill, KpiCard } from "../../components/WeldAssure";
import { hasPermission, PERMISSIONS } from "../../utils/hasPermission";

const TYPE_LABEL = { HEAT: "HEAT", PIECE: "PIECE", CONSUMABLE_LOT: "LOT" };

function when(v) {
  if (!v) return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function LabelsRegister() {
  const { loginUser } = useSelector((state) => state.userSlice);
  const userPermissions = loginUser?.decodedToken?.permissions ?? [];
  const canAct = hasPermission(userPermissions, PERMISSIONS.ADD_HEAT);

  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [action, setAction] = useState(null); // "void" | "reprint"
  const [reason, setReason] = useState("");
  const [actionError, setActionError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true); setError("");
    try {
      const res = await ApiCall.get("/labels");
      setLabels(res.data?.data?.labels || []);
    } catch (err) {
      setError(err?.response?.data?.message || "Could not load labels.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return labels.filter((l) => {
      if (filter === "voided" && !l.is_voided) return false;
      if (["HEAT", "PIECE", "CONSUMABLE_LOT"].includes(filter) && l.object_type !== filter) return false;
      if (!q) return true;
      return [l.object_label, l.object_detail, l.label_uuid, l.printed_by_name].some((v) => String(v || "").toLowerCase().includes(q));
    });
  }, [labels, filter, search]);

  const selected = labels.find((l) => l.id === selectedId) || shown[0] || null;
  const kpis = [
    { label: "LABELS PRINTED", value: labels.length },
    { label: "ACTIVE", value: labels.filter((l) => !l.is_voided).length },
    { label: "REPRINTS", value: labels.filter((l) => l.previous_label_id).length },
    { label: "VOIDED", value: labels.filter((l) => l.is_voided).length },
  ];

  const openAction = (kind) => { setAction(kind); setReason(""); setActionError(""); };
  const submitAction = async () => {
    if (!selected || !reason.trim()) return;
    setBusy(true); setActionError("");
    try {
      if (action === "void") await ApiCall.post(`/labels/${selected.id}/void`, { voidReason: reason.trim() });
      else await ApiCall.post(`/labels/${selected.id}/reprint`, { reason: reason.trim() });
      setAction(null);
      await load();
    } catch (err) {
      setActionError(err?.response?.data?.message || "That did not go through. Try again.");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Helmet><title>Labels | Shop Floor Mgmt</title></Helmet>
      <Container maxWidth="xl">
        <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mb: 2 }} spacing={2}>
          <Box>
            <Typography variant="h4">Labels</Typography>
            <Typography variant="body2" color="text.secondary">Every heat, piece and consumable label printed, reprinted or voided. Labels are never deleted.</Typography>
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <TextField size="small" label="Search" placeholder="Label, heat or piece" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ width: 260 }} />
            <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_, v) => v && setFilter(v)} aria-label="Filter labels">
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="HEAT">Heat</ToggleButton>
              <ToggleButton value="PIECE">Piece</ToggleButton>
              <ToggleButton value="CONSUMABLE_LOT">Lot</ToggleButton>
              <ToggleButton value="voided">Voided</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Stack>

        <PageGuide
          why="Know which labels are live on the floor, and that every reprint or void was done for a reason."
          who="Receivers, shop foremen and QC."
          what="Each label, what it is for, who printed it, and whether it is active or voided."
          how="Select a label to see it. Reprint or Void asks for a reason and is logged."
          when="When a label is damaged, wrong, or its material leaves the shop."
          standard="Material identification and traceability"
        />

        {loading && <LinearProgress sx={{ my: 2 }} />}
        {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}

        <Grid container spacing={2} sx={{ my: 1 }}>
          {kpis.map((k) => (<Grid item xs={6} md={3} key={k.label}><KpiCard label={k.label} value={k.value} /></Grid>))}
        </Grid>

        <Stack direction={{ xs: "column", lg: "row" }} spacing={2} alignItems="flex-start" sx={{ mb: 4 }}>
          <Card variant="outlined" sx={{ flexGrow: 1, width: "100%", overflowX: "auto" }}>
            <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 760, "& th": { bgcolor: "#1C2636", color: "#FFFFFF", fontSize: 11, fontWeight: 700, textAlign: "left", p: 1.5, letterSpacing: 0.3 }, "& td": { p: 1.5, borderTop: "1px solid #EEF0F3", fontSize: 14 } }}>
              <thead><tr><th>LABEL</th><th>RECORD</th><th>PRINTED BY</th><th>PRINTED</th><th>STATUS</th></tr></thead>
              <tbody>
                {!loading && shown.length === 0 && (<tr><td colSpan={5}>No labels match.</td></tr>)}
                {shown.map((l) => (
                  <Box component="tr" key={l.id} onClick={() => setSelectedId(l.id)} sx={{ cursor: "pointer", bgcolor: selected?.id === l.id ? "#FFF4E8" : "transparent", "&:hover": { bgcolor: "#F7F9FC" } }}>
                    <td style={{ fontFamily: "monospace" }}>{String(l.label_uuid || "").slice(0, 8)}</td>
                    <td><Box component="span" sx={{ fontFamily: "monospace", fontSize: 11, bgcolor: "#EEF0F3", borderRadius: 1, px: 0.75, mr: 1 }}>{TYPE_LABEL[l.object_type] || l.object_type}</Box>{l.object_label || `#${l.object_id}`}</td>
                    <td>{l.printed_by_name || ""}</td>
                    <td>{when(l.printed_at)}</td>
                    <td>{l.is_voided ? <Stack direction="row" spacing={1} alignItems="center"><StatusPill label="Voided" kind="danger" /><Typography variant="body2" color="text.secondary">{l.void_reason}</Typography></Stack> : <StatusPill label={l.previous_label_id ? "Active · reprint" : "Active"} kind="success" />}</td>
                  </Box>
                ))}
              </tbody>
            </Box>
          </Card>

          {selected && (
            <Card variant="outlined" sx={{ width: { xs: "100%", lg: 340 }, p: 2.5, flexShrink: 0 }} aria-label="Selected label">
              <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", letterSpacing: 0.4 }}>SELECTED LABEL</Typography>
              <Box sx={{ border: "2px solid #141A24", borderRadius: 2, p: 2, my: 1.5 }}>
                <Typography sx={{ fontFamily: "monospace", fontWeight: 600, fontSize: 18 }}>{TYPE_LABEL[selected.object_type] || selected.object_type} {selected.object_label || `#${selected.object_id}`}</Typography>
                <Typography variant="body2">{selected.object_detail || ""}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>{selected.label_uuid}</Typography>
              </Box>
              <Stack spacing={0.75} sx={{ fontSize: 14, mb: 2 }}>
                <Typography variant="body2"><strong>Printed by:</strong> {selected.printed_by_name || "not recorded"}{selected.printed_by_pos ? `, ${selected.printed_by_pos}` : ""}</Typography>
                <Typography variant="body2"><strong>Printed:</strong> {when(selected.printed_at)}</Typography>
                {selected.previous_label_id && <Typography variant="body2"><strong>Replaces:</strong> label #{selected.previous_label_id}</Typography>}
                {selected.is_voided ? <Typography variant="body2"><strong>Voided:</strong> {when(selected.voided_at)} by {selected.voided_by_name || "not recorded"}, {selected.void_reason}</Typography> : null}
              </Stack>
              {canAct && !selected.is_voided && (
                <Stack direction="row" spacing={1.5}>
                  <Button fullWidth variant="contained" onClick={() => openAction("reprint")} sx={{ bgcolor: "#F88F1D", color: "#231400", "&:hover": { bgcolor: "#E07F12" } }}>Reprint</Button>
                  <Button fullWidth variant="outlined" color="error" onClick={() => openAction("void")}>Void</Button>
                </Stack>
              )}
              <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1.5 }}>Reprint and void both ask for a reason and are logged. A voided label keeps its history.</Typography>
            </Card>
          )}
        </Stack>
      </Container>

      <Dialog open={!!action} onClose={() => !busy && setAction(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{action === "void" ? "Void label" : "Reprint label"} {selected?.object_label || ""}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>{action === "void" ? "The label stops scanning. This cannot be undone." : "A new label is printed. The old one stays on record."}</Typography>
          <TextField autoFocus fullWidth required label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} multiline minRows={2} />
          {actionError && <Alert severity="error" sx={{ mt: 2 }}>{actionError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAction(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" color={action === "void" ? "error" : "primary"} onClick={submitAction} disabled={busy || !reason.trim()}>{busy ? "Saving…" : action === "void" ? "Void label" : "Reprint"}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
